import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { computeGroupStats } from '../../utils/calculations';

export const BranchLeaderboard = () => {
  const { getScopedStudents } = useData();
  const [expandedBranches, setExpandedBranches] = useState({});

  const scoped = getScopedStudents();
  const branches = [...new Set(scoped.map(s => s.school).filter(Boolean))];

  if (branches.length === 0) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
        <h3 className="text-xl font-bold text-gray-800 mb-1">Branch Performance Leaderboard</h3>
        <p className="text-sm text-gray-400 italic">No data available.</p>
      </div>
    );
  }

  const toggleBranch = (idx) => {
    setExpandedBranches(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const rankedBranches = branches
    .map(branch => {
      const branchStudents = scoped.filter(s => s.school === branch);
      return {
        name: branch,
        students: branchStudents,
        stats: computeGroupStats(branchStudents)
      };
    })
    .sort((a, b) => b.stats.avgOverall - a.stats.avgOverall);

  const getMedal = (rankIndex) => {
    if (rankIndex === 0) return '🥇';
    if (rankIndex === 1) return '🥈';
    if (rankIndex === 2) return '🥉';
    return '🏅';
  };

  const pctOf = (v, n) => (n > 0 ? Math.round((v / n) * 100) + '%' : '0%');
  const scoreCell = (v, n, color) => (
    <td className="p-3 border font-bold" style={{ color: color }}>
      {n > 0 ? Math.round(v) + '%' : '—'}
    </td>
  );

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-1">Branch Performance Leaderboard</h3>
          <p className="text-sm text-gray-600">
            Ranked by overall average score.{' '}
            <span className="text-xs italic text-gray-500 font-bold">*Click a row to see its Grade 4 / Grade 6 breakdown.</span>
          </p>
          <p className="text-xs italic text-gray-400 mt-0.5">
            *A single weak level (60-79%) is forgiven if every other level is ≥80%.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-xs font-bold text-gray-700 bg-gray-50 p-2 rounded-lg border border-gray-100 w-fit">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: '#2a6a4a' }}></span>
            <span>At Grade Level</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: '#d4870a' }}></span>
            <span>Bridge the Gap</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: '#dc3545' }}></span>
            <span>Remediation Need</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full border-collapse text-sm text-center">
          <thead>
            <tr className="text-white text-xs font-bold">
              <th className="p-3 border text-left" style={{ background: '#1e3a5f' }}>RANK</th>
              <th className="p-3 border text-left" style={{ background: '#1e3a5f' }}>BRANCH</th>
              <th className="p-3 border" style={{ background: '#1e3a5f' }}>ASSESSED</th>
              <th className="p-3 border" style={{ background: '#1e3a5f' }}>OVERALL AVG</th>
              <th className="p-3 border" style={{ background: '#2a6a4a' }}>ENGLISH AVG</th>
              <th className="p-3 border" style={{ background: '#1e3a9a' }}>MATH AVG</th>
              <th className="p-3 border" style={{ background: '#2a6a4a' }}>AT GRADE LEVEL</th>
              <th className="p-3 border" style={{ background: '#d4870a' }}>BRIDGE</th>
              <th className="p-3 border" style={{ background: '#dc3545' }}>REMEDIATION</th>
            </tr>
          </thead>
          <tbody>
            {rankedBranches.map((item, i) => {
              const isOpen = !!expandedBranches[i];
              const uniqueGrades = [...new Set(item.students.map(s => s.registeredGrade))].sort((a, b) => a - b);
              const stats = item.stats;

              return (
                <React.Fragment key={item.name}>
                  {/* Branch Summary Row */}
                  <tr
                    onClick={() => toggleBranch(i)}
                    className="cursor-pointer hover:bg-gray-50 font-semibold border-b border-gray-200"
                  >
                    <td className="p-3 border text-lg">{getMedal(i)}</td>
                    <td className="p-3 border text-left text-gray-800">
                      <span className="inline-block mr-1">{isOpen ? '▾' : '▸'}</span>
                      {item.name}
                    </td>
                    <td className="p-3 border text-gray-700">
                      {stats.participated}/{stats.total}
                    </td>
                    {scoreCell(stats.avgOverall, stats.participated, '#1e3a5f')}
                    {scoreCell(stats.avgEnglish, stats.participated, '#2a6a4a')}
                    {scoreCell(stats.avgMath, stats.participated, '#1e3a9a')}
                    <td className="p-3 border" style={{ color: '#2a6a4a' }}>{pctOf(stats.comp, stats.participated)}</td>
                    <td className="p-3 border" style={{ color: '#d4870a' }}>{pctOf(stats.bridge, stats.participated)}</td>
                    <td className="p-3 border" style={{ color: '#dc3545' }}>{pctOf(stats.remed, stats.participated)}</td>
                  </tr>

                  {/* Expanded Grade Breakdown Rows */}
                  {isOpen && uniqueGrades.map((g) => {
                    const gStudents = item.students.filter(s => s.registeredGrade === g);
                    const gStats = computeGroupStats(gStudents);

                    return (
                      <tr key={g} className="bg-gray-50 text-xs border-b border-gray-100">
                        <td className="p-2 border text-left pl-8 text-gray-500" colSpan={2}>
                          ↳ Grade {g}
                        </td>
                        <td className="p-2 border text-gray-600">
                          {gStats.participated}/{gStats.total}
                        </td>
                        {scoreCell(gStats.avgOverall, gStats.participated, '#1e3a5f')}
                        {scoreCell(gStats.avgEnglish, gStats.participated, '#2a6a4a')}
                        {scoreCell(gStats.avgMath, gStats.participated, '#1e3a9a')}
                        <td className="p-2 border text-gray-600">{pctOf(gStats.comp, gStats.participated)}</td>
                        <td className="p-2 border text-gray-600">{pctOf(gStats.bridge, gStats.participated)}</td>
                        <td className="p-2 border text-gray-600">{pctOf(gStats.remed, gStats.participated)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default BranchLeaderboard;
